// Settings — ported from Luan's #app-settings (tab-pills + settings-card panels).
// Appearance (real useTheme) and Notifications (real api.notificationPrefs/updateNotificationPrefs)
// are fully functional. Security is owned by Cognito (sign-in / password reset live on the auth
// screens; patient MFA is opt-in at the pool, no in-app 2FA UI), so it's a clearly-inert panel that
// points at account security rather than pretending to persist. Language is Pass 2 (no locale
// backend yet) — shown read-only/disabled. Quiet-hours inputs intentionally removed. All non-PHI.
import { useEffect, useState } from "react";
import { useTheme } from "@championcare/ui";
import { useApi } from "../api";
import { useAsync } from "../lib/useAsync";
import { Loading, ErrorState } from "../components/ui";
import type { NotificationPrefs } from "@forcebase-ai/schema/patient";

type Tab = "appearance" | "notifications" | "security" | "language";

function Toggle({ on, onClick, label }: { on: boolean; onClick: () => void; label: string }) {
  return (
    <button type="button" className={`toggle ${on ? "on" : ""}`} role="switch" aria-checked={on} aria-label={label} onClick={onClick} />
  );
}

export function Settings() {
  const api = useApi();
  const { theme, setTheme } = useTheme();
  const { data, loading, error } = useAsync(() => api.notificationPrefs(), []);
  const [tab, setTab] = useState<Tab>("appearance");
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (data) setPrefs(data);
  }, [data]);

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} />;

  function set<K extends keyof NotificationPrefs>(key: K, value: NotificationPrefs[K]) {
    setPrefs((p) => (p ? { ...p, [key]: value } : p));
    setSaved(false);
  }

  async function save() {
    if (!prefs) return;
    setBusy(true);
    try {
      const next = await api.updateNotificationPrefs(prefs);
      setPrefs(next);
      setSaved(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="app-page-head">
        <h1>Settings.</h1>
        <p>Appearance, notifications, security, and language preferences.</p>
      </div>

      <div className="tab-pills" role="tablist">
        <button className={`tab-pill ${tab === "appearance" ? "active" : ""}`} onClick={() => setTab("appearance")} role="tab">Appearance</button>
        <button className={`tab-pill ${tab === "notifications" ? "active" : ""}`} onClick={() => setTab("notifications")} role="tab">Notifications</button>
        <button className={`tab-pill ${tab === "security" ? "active" : ""}`} onClick={() => setTab("security")} role="tab">Security</button>
        <button className={`tab-pill ${tab === "language" ? "active" : ""}`} onClick={() => setTab("language")} role="tab">Language</button>
      </div>

      {tab === "appearance" && (
        <div className="settings-card">
          <h3>Theme</h3>
          <p>Choose how Champion Care looks. Your selection is saved on this device.</p>
          <div className="theme-tiles">
            <button className={`theme-tile light ${theme === "light" ? "selected" : ""}`} onClick={() => setTheme("light")}>
              <div className="theme-tile-preview" />
              <div className="theme-tile-label">Light</div>
            </button>
            <button className={`theme-tile dark ${theme === "dark" ? "selected" : ""}`} onClick={() => setTheme("dark")}>
              <div className="theme-tile-preview" />
              <div className="theme-tile-label">Dark</div>
            </button>
          </div>
        </div>
      )}

      {tab === "notifications" && (
        <div className="settings-card">
          <h3>Notifications</h3>
          <p>How we reach you about visits and messages.</p>
          {prefs && (
            <>
              <div className="settings-row">
                <div className="settings-row-text">
                  <div className="settings-row-title">Email notifications</div>
                  <div className="settings-row-desc">Visit reminders and message alerts by email.</div>
                </div>
                <Toggle on={prefs.email} onClick={() => set("email", !prefs.email)} label="Email notifications" />
              </div>
              <div className="settings-row">
                <div className="settings-row-text">
                  <div className="settings-row-title">Push notifications</div>
                  <div className="settings-row-desc">Alerts on your devices.</div>
                </div>
                <Toggle on={prefs.push} onClick={() => set("push", !prefs.push)} label="Push notifications" />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 18 }}>
                <button className="btn btn-primary" disabled={busy} onClick={save}>
                  {busy ? "Saving…" : "Save preferences"}
                </button>
                {saved && <span className="settings-row-desc">Saved.</span>}
              </div>
            </>
          )}
        </div>
      )}

      {tab === "security" && (
        <div className="settings-card">
          <h3>Security</h3>
          <p>Sign-in, password, and two-factor authentication are managed through your secure account.</p>
          <div className="settings-row">
            <div className="settings-row-text">
              <div className="settings-row-title">Password</div>
              <div className="settings-row-desc">Update your password from your account security page.</div>
            </div>
            <button className="btn btn-ghost btn-sm" disabled>Managed by account</button>
          </div>
          <div className="settings-row">
            <div className="settings-row-text">
              <div className="settings-row-title">Two-factor authentication</div>
              <div className="settings-row-desc">Add an extra layer of protection to your account.</div>
            </div>
            <button className="btn btn-ghost btn-sm" disabled>Managed by account</button>
          </div>
          <div className="settings-row">
            <div className="settings-row-text">
              <div className="settings-row-title">Sign-in activity</div>
              <div className="settings-row-desc">Review recent sign-ins to your account.</div>
            </div>
            <button className="btn btn-ghost btn-sm" disabled>Managed by account</button>
          </div>
        </div>
      )}

      {tab === "language" && (
        <div className="settings-card">
          <h3>Language &amp; region</h3>
          <p>Champion Care is currently available in English (US). More languages are coming soon.</p>
          <div className="field-row">
            <div className="field">
              <label className="field-label">Language</label>
              <input className="field-input" value="English (US)" readOnly />
            </div>
            <div className="field">
              <label className="field-label">Time zone</label>
              <input className="field-input" value={Intl.DateTimeFormat().resolvedOptions().timeZone} readOnly />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
